import {
  ELABEL_COLOURS,
  ELABEL_PROVIDERS,
  ELABEL_STATUSES,
  isScantrustApiKey,
  isScantrustUid,
  type ELabelFacts,
} from "@proef/domain";
import { z } from "zod";

const nutrient = z.number().min(0).max(100_000).nullable();

/**
 * A producer's e-label as a provider's parser (@proef/domain) reduces it. One shape for the PWA, the
 * edge and druif's ingestion; `null` or empty always means "the producer did not state it", and
 * status `empty` is a valid result for a label the producer has not filled in yet.
 */
export const ELabel = z.object({
  provider: z.enum(ELABEL_PROVIDERS),
  status: z.enum(ELABEL_STATUSES),
  sku: z.string().min(1).max(200).describe("The provider's public record id; never a secret"),
  lang: z
    .string()
    .regex(/^[a-z]{2,3}$/)
    .nullable()
    .describe("The language the text was read in; null when the provider has no language choice"),
  productName: z.string().max(300),
  producerName: z.string().max(300),
  originCountries: z
    .array(z.string().regex(/^[A-Z]{2}$/))
    .max(20)
    .describe("ISO 3166-1 alpha-2"),
  colour: z.enum(ELABEL_COLOURS),
  grapes: z.array(z.string().min(1).max(100)).max(30),
  vintage: z.number().int().min(1800).max(2100).nullable(),
  abvPercent: z.number().min(0).max(100).nullable(),
  volumeMl: z.number().positive().max(100_000).nullable(),
  ingredients: z.object({
    items: z.array(z.string().min(1).max(200)).max(60),
    /** The producer's own wording per language (lowercase code), HTML stripped. */
    freeText: z
      .record(z.string().regex(/^[a-z]{2,3}(?:-[a-z0-9]{2,8})?$/), z.string().max(4000))
      .refine((texts) => Object.keys(texts).length <= 40, "too many languages"),
  }),
  nutritionPer100ml: z.object({
    energyKj: nutrient,
    energyKcal: nutrient,
    fatG: nutrient,
    saturatedFatG: nutrient,
    carbohydrateG: nutrient,
    sugarG: nutrient,
    proteinG: nutrient,
    saltG: nutrient,
    fibreG: nutrient,
  }),
  allergens: z.array(z.string().min(1).max(200)).max(30),
  recycling: z
    .array(
      z.object({
        component: z.string().max(100),
        material: z.string().max(100),
        instruction: z.string().max(300),
      }),
    )
    .max(20),
  servingMl: z.number().positive().max(10_000).nullable(),
  sourceUpdatedAt: z.string().max(40).nullable(),
});
export type ELabel = z.infer<typeof ELabel>;

// The domain parser and this schema describe one shape; this fails the build if they drift.
type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
export const ELABEL_SHAPES_MATCH: Same<ELabel, ELabelFacts> = true;

/** No space, control character or DEL: the link goes into a request line and must not be able to end it. */
function isPrintable(text: string): boolean {
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code <= 0x20 || code === 0x7f) return false;
  }
  return true;
}

/**
 * What the edge verifies an upload with: the link the phone read, exactly as the registry rebuilt it
 * (@proef/domain `ELabelTarget.url`). For Scantrust it carries the api key, so the edge uses it in
 * memory only: never stored, logged or echoed.
 */
export const ELabelProof = z.object({
  url: z
    .string()
    .min(1)
    .max(2048)
    .refine(
      (url) => url.startsWith("https://") && isPrintable(url),
      "url must be a plain https link",
    ),
});
export type ELabelProof = z.infer<typeof ELabelProof>;

/** `POST /v1/elabels`: the phone's parsed label plus the proof the edge verifies it with. */
export const ELabelUpload = z.object({ elabel: ELabel, proof: ELabelProof });
export type ELabelUpload = z.infer<typeof ELabelUpload>;

/**
 * `known` = an identical label was already verified (nothing re-fetched); `accepted` = queued
 * for verification. Verification itself is asynchronous: a rejected claim is dropped, not reported.
 */
export const ELabelUploadResponse = z.object({ status: z.enum(["accepted", "known"]) });
export type ELabelUploadResponse = z.infer<typeof ELabelUploadResponse>;

/** Max body of `POST /v1/elabels`, in bytes. */
export const ELABEL_UPLOAD_MAX_BYTES = 65_536;

/** `POST /v1/elabels/resolve`: a scanned short link (e.g. u-label.com) the edge resolves to a proof. */
export const ELabelResolveRequest = z.object({ url: z.string().min(1).max(2048) });
export type ELabelResolveRequest = z.infer<typeof ELabelResolveRequest>;

/** The proof the short link redirected to, for the phone's own Scantrust call; never stored by the edge. */
export const ELabelResolveResponse = z.object({
  uid: z.string().refine(isScantrustUid, "uid must be a UUID"),
  apiKey: z.string().refine(isScantrustApiKey, "apiKey must be visible ASCII, 1-200 chars"),
  sku: z.string().regex(/^[A-Za-z0-9._-]{1,64}$/),
});
export type ELabelResolveResponse = z.infer<typeof ELabelResolveResponse>;

/** `POST /v1/elabels/relay`: a dumb, allowlisted byte relay for a page a browser may not read (no CORS). */
export const ELabelRelayRequest = z.object({ url: z.string().min(1).max(2048) });
export type ELabelRelayRequest = z.infer<typeof ELabelRelayRequest>;

/**
 * Exactly what the relay returns and nothing it interpreted: where the chain ended, the hosts it
 * visited (never a full URL, which may hold a key), the last status, its content type and the body
 * as text. The phone parses the text itself.
 */
export const ELabelRelayResponse = z.object({
  finalUrl: z.string().max(2048),
  hopHosts: z.array(z.string().max(253)).max(8),
  status: z.number().int().min(100).max(599),
  contentType: z.string().max(200),
  text: z.string(),
});
export type ELabelRelayResponse = z.infer<typeof ELabelRelayResponse>;

/**
 * `POST /v1/elabel/unseen`: a scanned web link no provider owns. The phone sends the host and nothing
 * else of the link; `asked` is the user's explicit "ask us to support this", which counts as a vote
 * even when the passive report was already made. The edge validates the host again.
 */
export const ELabelUnseenRequest = z.object({
  host: z.string().min(1).max(260),
  asked: z.boolean().optional(),
});
export type ELabelUnseenRequest = z.infer<typeof ELabelUnseenRequest>;

/** `recorded` is false when the host is not one to record (an IP, a private name, a known provider): nothing was kept. */
export const ELabelUnseenResponse = z.object({ recorded: z.boolean() });
export type ELabelUnseenResponse = z.infer<typeof ELabelUnseenResponse>;
